import { useState } from 'react'

import classnames from 'classnames'

import { ButtonVariants } from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import Ctas from '~/components/atoms/Ctas/Ctas'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { LinkProps } from '~/components/links'
import { ComponentTheme } from '~/components/themes'
import { ButtonVariants as EnumButtonVariants } from '~/enums/buttonVariants'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './Spotlight.module.css'

interface SpotlightSlides {
  image: ImageProps
  pretitle: string
  title: string
  points: string[]
  text: string
  link: LinkProps
  linkText?: string
}

export interface SpotlightProps {
  items: SpotlightSlides[]
  title: string
  variant?: ComponentTheme
  moduleId?: string
}

export default function Spotlight({
  items,
  title,
  variant = ComponentTheme.primary,
  moduleId,
}: SpotlightProps) {
  const [overlayImage, setOverlayImage] = useState(items[0].image)
  const isMobile = useMediaQuery(`(max-width: ${bp.bpLarge})`)

  const theme = variant
  let baseThemeClass = theme

  if (theme === ComponentTheme.magazine)
    baseThemeClass = ComponentTheme.magazine

  const onSlideChange = (itemProps: { activeIndex: number }) => {
    const index = itemProps.activeIndex
    setOverlayImage(items[index].image)
  }

  return (
    <div
      className={classnames(
        styles.base,
        baseThemeClass && styles[baseThemeClass]
      )}
    >
      {overlayImage && (
        <div className={styles.backgroundImageWrap}>
          <Image
            asset={overlayImage?.asset}
            className={styles.backgroundImage}
            objectFit="cover"
            width={60}
          />
        </div>
      )}
      <Fixture
        theme={theme}
        verticalPadding={theme !== ComponentTheme.magazine}
      >
        <div style={{ overflow: 'hidden' }}>
          <div className={styles.wrap}>
            <Container
              cssClass={classnames(styles.sliderContainer, styles[theme])}
            >
              {isMobile && (
                <Text
                  size={TextSizes.secondaryTitleLrg}
                  uppercase
                  className={styles.title}
                >
                  {title}
                </Text>
              )}
              <DynamicSlider
                slidesPerView={1}
                slidesPerViewMed={1}
                slidesPerViewLrg={1}
                overflowVisible={false}
                sectionId={moduleId}
                hideTitle={true}
                hideArrows={false}
                hideArrowsMobile={true}
                useScrollbar
                spacer={false}
                wrapperClass={styles.sliderWrapperClass}
                initiateInitialSlide={true}
                onSlideChange={onSlideChange}
              >
                {items?.map((item: SpotlightSlides, i: number) => {
                  return (
                    <div key={`spotlight-item-${i}`} className={styles.slide}>
                      <div className={styles.slideContent}>
                        {!isMobile && (
                          <Text
                            size={TextSizes.secondaryTitleLrg}
                            uppercase
                            className={styles.title}
                          >
                            {title}
                          </Text>
                        )}
                        <Text size={TextSizes.primaryTitleSml}>
                          {item.pretitle}
                        </Text>
                        <Text
                          Tag="h3"
                          size={TextSizes.secondaryTitleXLrg}
                          className={styles.slideTitle}
                        >
                          {item.title}
                        </Text>
                        {item.points && (
                          <div className={styles.slidePoints}>
                            {item.points.map((point, idx) => (
                              <Text
                                key={`slide-${i}-${idx}-point`}
                                className={styles.slidePoint}
                                size={TextSizes.bodyMed}
                              >
                                {point}
                              </Text>
                            ))}
                          </div>
                        )}
                        {isMobile && (
                          <div className={styles.slideImageContainer}>
                            <div className={styles.slideImage}>
                              <Image
                                {...item.image}
                                alt={item.title}
                                objectFit="contain"
                              />
                            </div>
                          </div>
                        )}
                        <Text size={TextSizes.bodyLrg} className={styles.text}>
                          {item.text}
                        </Text>
                        {item.link && (
                          <div className={styles.slideLink}>
                            <Ctas
                              ctas={[
                                {
                                  linkText: item.linkText || `Read more`,
                                  children: [item.linkText || `Read more`],
                                  ...item.link,
                                  link: item.link as any,
                                  buttonStyle: EnumButtonVariants.primary,
                                  variant: ButtonVariants.primaryDark,
                                },
                              ]}
                              theme={ComponentTheme.primary}
                            />
                          </div>
                        )}
                      </div>
                      {!isMobile && (
                        <div className={styles.slideImageContainer}>
                          <div className={styles.slideImage}>
                            <Image
                              {...item.image}
                              alt={item.title}
                              objectFit="contain"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </DynamicSlider>
            </Container>
          </div>
        </div>
      </Fixture>
    </div>
  )
}
